import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import Layout from 'components/Layout';
import {
  Grid,
  Typography,
  Button as MuiButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
  IconButton,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { useMst } from 'model/Root';
import { useTranslation } from 'react-i18next';
import { OCR_MAX_PDF_BYTES } from 'config/constants';
import type { OcrStoreInstance } from 'model/CoreSkills/OcrStore';

const OcrScreen = observer(() => {
  const { t } = useTranslation();
  const rootStore = useMst();
  const { errorStore, ocrStore } = rootStore;
  const { itemsArray, isLoading, isUploading, list, uploadPdf, remove } = ocrStore as OcrStoreInstance;
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // Deliberately avoid auto-loading to prevent instant network error toast if backend not ready.

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      errorStore.setError('Invalid file type');
      return;
    }
    if (file.size > OCR_MAX_PDF_BYTES) {
      errorStore.setError('File too large');
      return;
    }
    setSelectedFile(file);
  };

  const onUpload = async () => {
    if (selectedFile && uploadPdf) {
      await uploadPdf(selectedFile, { document_type: 'audiobook' });
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      // Keep ordering consistent and reset visible batch
      if (list) await list({ documentType: 'audiobook', limit: 50 });
      setVisibleCount(10);
    }
  };

  type OcrDoc = { id: string; filename?: string; created_at?: string; updated_at?: string; size?: number | null };
  type SortMode = 'date_desc' | 'date_asc' | 'name_asc' | 'name_desc';
  const [visibleCount, setVisibleCount] = useState(10);
  const [sortMode, setSortMode] = useState<SortMode>('date_desc');
  const [search, setSearch] = useState('');

  const filteredDocs = useMemo(() => {
    const arr = (Array.isArray(itemsArray) ? [...itemsArray] : []) as OcrDoc[];
    const q = search.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter((d) =>
      String(d.filename || '')
        .toLowerCase()
        .includes(q)
    );
  }, [itemsArray, search]);

  const sortedDocs = useMemo(() => {
    const arr = [...filteredDocs] as OcrDoc[];
    const byDate = (d: OcrDoc) => Date.parse(d.updated_at || d.created_at || '') || 0;
    const byName = (d: OcrDoc) => String(d.filename || '').toLowerCase();
    arr.sort((a, b) => {
      if (sortMode === 'name_asc' || sortMode === 'name_desc') {
        const na = byName(a);
        const nb = byName(b);
        if (na !== nb) return sortMode === 'name_asc' ? na.localeCompare(nb) : nb.localeCompare(na);
        // tie-breaker by date desc for stability
        return (
          (Date.parse(b.updated_at || b.created_at || '') || 0) - (Date.parse(a.updated_at || a.created_at || '') || 0)
        );
      }
      const da = byDate(a);
      const db = byDate(b);
      if (da !== db) return sortMode === 'date_desc' ? db - da : da - db;
      // tie-breaker by name asc for stability
      return byName(a).localeCompare(byName(b));
    });
    return arr;
  }, [filteredDocs, sortMode]);
  const visibleDocs = useMemo(() => sortedDocs.slice(0, visibleCount), [sortedDocs, visibleCount]);

  const [fetchedOnce, setFetchedOnce] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (list) await list({ documentType: 'audiobook' });
      } finally {
        if (mounted) setFetchedOnce(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [list]);

  return (
    <Layout title={'Kirjan PDF‑tiedostot'}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <input ref={fileInputRef} type="file" accept="application/pdf" onChange={onPickFile} />
          <MuiButton variant="contained" onClick={onUpload} disabled={!selectedFile || isUploading} sx={{ ml: 2 }}>
            {isUploading ? t('loading') : t('upload')}
          </MuiButton>
          <IconButton
            color="default"
            onClick={() => list && list({ documentType: 'audiobook', limit: 50 })}
            sx={{ ml: 2 }}
            aria-label="refresh"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
          <Select
            size="small"
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value as SortMode)}
            sx={{ ml: 2, minWidth: 180 }}
          >
            <MenuItem value="date_desc">Uusin ensin</MenuItem>
            <MenuItem value="date_asc">Vanhin ensin</MenuItem>
            <MenuItem value="name_asc">Nimi A–Ö</MenuItem>
            <MenuItem value="name_desc">Nimi Ö–A</MenuItem>
          </Select>
          <TextField
            size="small"
            placeholder="Hae tiedostoja"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ ml: 2, minWidth: 240 }}
          />
          {fetchedOnce && isLoading && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, ml: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="GrayText">
                Refreshing…
              </Typography>
            </Box>
          )}
        </Grid>

        {!fetchedOnce && isLoading ? (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body1">{t('loading')}</Typography>
            </Box>
          </Grid>
        ) : (
          <>
            {visibleDocs.map((doc: OcrDoc) => (
              <Grid item xs={12} key={doc.id}>
                <Box sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, p: 1.5 }}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                      {doc.filename}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setConfirmDeleteId(String(doc.id))}
                      aria-label="delete"
                      disabled={isDeleting}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="caption" color="GrayText">
                    {doc.size ? `${Math.round(doc.size / 1024)} KB` : ''} {doc.created_at ?? ''}
                  </Typography>
                </Box>
              </Grid>
            ))}
            {sortedDocs.length === 0 && !isLoading && (
              <Grid item xs={12}>
                <Typography variant="body2" color="GrayText">
                  {t('api_not_ready_message')}
                </Typography>
              </Grid>
            )}
            {sortedDocs.length > visibleCount && (
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                  <MuiButton size="small" variant="outlined" onClick={() => setVisibleCount((v) => v + 10)}>
                    Lataa lisää
                  </MuiButton>
                </Box>
              </Grid>
            )}
          </>
        )}
      </Grid>

      <Dialog open={!!confirmDeleteId} onClose={() => (isDeleting ? void 0 : setConfirmDeleteId(null))}>
        <DialogTitle>Poista kirjan PDF‑tiedosto?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Tämä poistaa tiedoston pysyvästi. Haluatko jatkaa?</Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton disabled={isDeleting} onClick={() => setConfirmDeleteId(null)}>
            Peruuta
          </MuiButton>
          <MuiButton
            color="error"
            variant="contained"
            disabled={isDeleting}
            onClick={async () => {
              if (!confirmDeleteId || !remove) return;
              setIsDeleting(true);
              try {
                await remove(confirmDeleteId);
                setConfirmDeleteId(null);
                if (list) void list({ documentType: 'audiobook' });
              } finally {
                setIsDeleting(false);
              }
            }}
          >
            {isDeleting ? <CircularProgress size={16} /> : 'Poista'}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Layout>
  );
});

export default OcrScreen;
